import { CanActivateFn, Router, Routes } from '@angular/router';
import {
  getEmailCodeResponse,
  getInvoices,
  getOperations,
  getPrivacyPolicy,
  getTableSettings,
  getTermsOfUse,
  subscribedWallets
} from './partials/resolvers';
import {
  MonitoringTableSettings,
  tableName as monitoringTableName
} from "./pages/monitoring/components/transactions/components/monitoring-table/monitoring-table.component";
import {
  ReportsTableSettings,
  tableName as reportsTableName
} from "./pages/monitoring/components/reports/components/reports-table/reports-table.component";
import { environment } from "../environments/environment";
import { inject } from "@angular/core";
import { AuthService } from "./partials/services/auth.service";
import { checkQueryParamsGuard } from "./partials/guards/check-query-params.guard";
import { AccountType, CompanyRole } from "./api/api.model";

const withAccountGuard: CanActivateFn = () => {
  const authService: AuthService = inject(AuthService);
  const router: Router = inject(Router);
  return authService.accountDto() ? true : router.createUrlTree(['/login']);
};

const withoutAccountGuard: CanActivateFn = () => {
  const authService: AuthService = inject(AuthService);
  const router: Router = inject(Router);
  return authService.accountDto() ? router.createUrlTree(['/']) : true;
};

const accountGuard = (allowCompanyRoles: CompanyRole[]): CanActivateFn => () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const account = authService.accountDto();
  if (!account) {
    return router.createUrlTree(['/']);
  }

  if (account.accountType === AccountType.PERSONAL) {
    return true;
  }

  if (
    account.accountType === AccountType.CORPORATE &&
    allowCompanyRoles.includes(account.companyRole)
  ) {
    return true;
  }

  return router.createUrlTree(['/']);
};

const devGuard: CanActivateFn = () => !environment.production && environment.devMode;

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then(({ LayoutComponent }) => LayoutComponent),
    children: [
      {
        path: '',
        canActivate: [withAccountGuard],
        loadComponent: () => import('./pages/wallets-page/wallets-page.component').then(({ WalletsPageComponent }) => WalletsPageComponent)
      },
      {
        path: 'monitoring',
        canActivate: [withAccountGuard],
        resolve: {
          transactionTableSettings: getTableSettings<MonitoringTableSettings>(monitoringTableName),
          reportsTableSettings: getTableSettings<ReportsTableSettings>(reportsTableName)
        },
        loadComponent: () => import('./pages/monitoring/monitoring.component').then(({ MonitoringComponent }) => MonitoringComponent)
      },
      {
        path: 'balance',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE, CompanyRole.AML_OFFICER])],
        resolve: {
          getOperations,
          getInvoices,
        },
        loadComponent: () => import('./pages/balance/balance.component').then(({ BalanceComponent }) => BalanceComponent)
      },
      {
        path: 'pricing',
        canActivate: [withAccountGuard],
        loadComponent: () => import('./pages/pricing/pricing.component').then(({ PricingComponent }) => PricingComponent)
      },
      {
        path: 'api',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE])],
        loadComponent: () => import('./pages/api/api.component').then(({ ApiComponent }) => ApiComponent)
      },
      {
        path: 'account-upgrade',
        canActivate: [withAccountGuard],
        loadComponent: () => import('./pages/profile/account-upgrade/account-upgrade.component').then(({ AccountUpgradeComponent }) => AccountUpgradeComponent)
      },
      {
        path: 'manage-team-members',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE])],
        loadComponent: () => import('./pages/profile/manage-team-members/manage-team-members.component').then(({ ManageTeamMembersComponent }) => ManageTeamMembersComponent)
      },
      {
        path: 'privacy-policy',
        resolve: {
          response: getPrivacyPolicy
        },
        loadComponent: () => import('./pages/docs/privacy-policy/privacy-policy.component').then(({ PrivacyPolicyComponent }) => PrivacyPolicyComponent)
      },
      {
        path: 'faq',
        loadComponent: () => import('./pages/docs/faq/faq.component').then(({ FaqComponent }) => FaqComponent)
      },
      {
        path: 'terms-of-service',
        resolve: {
          response: getTermsOfUse
        },
        loadComponent: () => import('./pages/docs/terms-of-service/terms-of-service.component').then(({ TermsOfServiceComponent }) => TermsOfServiceComponent)
      },
      {
        path: 'wiki',
        loadComponent: () => import('./pages/wiki/wiki.component').then(({ WikiComponent }) => WikiComponent)
      },
      {
        path: 'how-cluster-works',
        loadComponent: () => import('./pages/how-cluster-works/how-cluster-works.component').then(({ HowClusterWorksComponent }) => HowClusterWorksComponent)
      },
      {
        path: '404',
        loadComponent: () => import('./pages/404/404.component').then(({ Page404Component }) => Page404Component)
      },
      {
        path: '401',
        loadComponent: () => import('./pages/401/401.component').then(({ Page401Component }) => Page401Component)
      },
    ]
  },
  {
    path: '',
    canActivate: [withoutAccountGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./pages/auth/login/login.component').then(({ LoginComponent }) => LoginComponent)
      },
      {
        path: 'choose-account',
        loadComponent: () => import('./pages/auth/choose-account/choose-account.component').then(({ ChooseAccountComponent }) => ChooseAccountComponent)
      },
      {
        path: 'password-recovery',
        loadComponent: () => import('./pages/auth/password-recovery/password-recovery.component').then(({ PasswordRecoveryComponent }) => PasswordRecoveryComponent)
      },
      {
        path: 'register-corporate-account',
        loadComponent: () => import('./pages/auth/register-corporate-account/register-corporate-account.component').then(({ RegisterCorporateAccountComponent }) => RegisterCorporateAccountComponent)
      },
      {
        path: 'invitee-login',
        canActivate: [checkQueryParamsGuard(['inviteCode'])],
        resolve: {
          invitationCodeData: getEmailCodeResponse
        },
        loadComponent: () => import('./pages/auth/invitee-login/invitee-login.component').then(({ InviteeLoginComponent }) => InviteeLoginComponent)
      },
    ]
  },
  {
    path: 'invitee-login-has-account',
    canActivate: [checkQueryParamsGuard(['inviteCode'])],
    resolve: {
      invitationCodeData: getEmailCodeResponse
    },
    loadComponent: () => import('./pages/auth/invitee-login-has-account/invitee-login-has-account.component').then(({ InviteeLoginHasAccountComponent }) => InviteeLoginHasAccountComponent)
  },
  {
    path: 'all-svg-icons',
    loadComponent: () => import('./partials/dev/show-all-svg-icons/show-all-svg-icons.component').then(({ ShowAllSvgIconsComponent }) => ShowAllSvgIconsComponent),
    canActivate: [devGuard],
  },
  {
    path: '**',
    redirectTo: '404'
  }
];
